import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ReceiptModal from "./ReceiptModal";
import PatientHistoryModal from "../components/PatientHistoryModal";
import { customFetch } from '../apiClient';

const initialForm = { 
  uhidNo: "",
  name: "",
  mobile: "",
  registrationDate: new Date().toISOString().split("T")[0],
  gender: "Male",
  age: "",
  sendBy: "", 
  remarks: "", 
  ageIn: "Years",
  title: "",
  email: "",
  careOf: "",
  relationWithPatient: "Father",
  careOfMobile: "",
  appointmentReason: "",
  address: "",
  policeStation: "",
  city: "",
  state: "",
  pincode: "",
  departmentId: "",
  doctorId: "",
  rmoId: "",
  referredBy: "",
  weight: "",
  height: "",
  bp: "",
  oxygen: "",
  pulse: "",
  temperature: "",
  rbs: "",
  consultFee: "",
  discount: "",
  payable: "",
  paid: "",
  paymentMode: "Cash",
  due: "",
  discountAuthority: "",
  requestedBy: "",
  packageId: "", // ✅ New Field for IPD Package
};

const titleOptions = [
  { value: "Mr", label: "Mr" },
  { value: "Mrs", label: "Mrs" },
  { value: "Miss", label: "Miss" },
  { value: "Ms", label: "Ms" },
  { value: "Master", label: "Master" },
  { value: "Baby", label: "Baby" },
  { value: "Dr", label: "Dr" }
];

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" }
];

const ageInOptions = [
  { value: "Years", label: "Years" },
  { value: "Months", label: "Months" },
  { value: "Days", label: "Days" }
];

const relationOptions = [
  { value: "Father", label: "Father" },
  { value: "Mother", label: "Mother" },
  { value: "Spouse", label: "Spouse" },
  { value: "Sibling", label: "Sibling" },
  { value: "Other", label: "Other" }
];

const paymentModeOptions = [
  { value: "Cash", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "Card", label: "Card" }
];

const PatientRegister = () => {
  const navigate = useNavigate();
  
  const clamp = (min, preferred, max) => {
    const vw = window.innerWidth / 100;
    const calculated = preferred * vw;
    return Math.min(Math.max(calculated, min), max);
  };
  
  const [form, setForm] = useState(initialForm);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [rmos, setRmos] = useState([]); 
  const [packages, setPackages] = useState([]); // ✅ State for Active Packages
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState({ 
    departments: false, 
    doctors: false, 
    rmos: false, 
    states: false, 
    districts: false,
    packages: false // ✅ Loading state for packages
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [patientType, setPatientType] = useState("OPD");
  const [showReceipt, setShowReceipt] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [discountAuthorities, setDiscountAuthorities] = useState([]);
  const [loadingAuthorities, setLoadingAuthorities] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchUhid, setSearchUhid] = useState("");

 

  const formRef = useRef(null);
  const opdRadioRef = useRef(null);

  const titleGenderMap = {
    "Mr": "Male", "Master": "Male", "Mrs": "Female", 
    "Miss": "Female", "Ms": "Female", "Baby": "Other", "Dr": "",
  };

  useEffect(() => {
    if (opdRadioRef.current) opdRadioRef.current.focus();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoadingMeta(m => ({ ...m, states: true }));
        const res = await customFetch(`/District/states`);
        if (!res.ok) throw new Error("Failed to load states");
        const data = await res.json();
        setStates(data.map(s => ({ value: s, label: s })));
      } catch (err) { console.error(err); } 
      finally { setLoadingMeta(m => ({ ...m, states: false })); }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!selectedState) { setDistricts([]); setSelectedDistrict(null); return; }
      try {
        setLoadingMeta(m => ({ ...m, districts: true }));
        const res = await customFetch(`/District/districts?stateName=${encodeURIComponent(selectedState.value)}`);
        if (!res.ok) throw new Error("Failed to load districts");
        const data = await res.json();
        setDistricts(data.map(d => ({ value: d.district_Name, label: d.district_Name, id: d.districtID, stateId: d.stateID })));
      } catch (err) { console.error(err); } 
      finally { setLoadingMeta(m => ({ ...m, districts: false })); }
    };
    loadDistricts();
  }, [selectedState]);

 // ✅ YAHAN FRONTEND FILTER LAGAYA GAYA HAI (isActive === true)
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setLoadingMeta(m => ({ ...m, departments: true }));
        const res = await customFetch(`/Departments`);
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        
        // Filter active departments only before setting to state
        const activeDepts = data
          .filter(d => d.isActive === true)
          .map(d => ({ value: d.srno, label: d.departmentName }));
          
        setDepartments(activeDepts);
      } catch (err) { setError(err.message); } 
      finally { setLoadingMeta(m => ({ ...m, departments: false })); }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoadingMeta(m => ({ ...m, doctors: true }));
        const res = await customFetch(`/Employees/doctors`);
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        
        setDoctors(data.map(d => {
          let labelText = d.name;
          if (d.degree) labelText += ` (${d.degree})`;
          if (d.department?.departmentName) labelText += ` - ${d.department.departmentName}`;
          
          return { value: d.id, label: labelText };
        }));
      } catch (err) { setError(err.message); } 
      finally { setLoadingMeta(m => ({ ...m, doctors: false })); }
    };
    loadDoctors();
  }, []);

  useEffect(() => {
    const loadRMOs = async () => {
      try {
        setLoadingMeta(m => ({ ...m, rmos: true }));
        const res = await customFetch(`/Employees/rmos`);
        if (!res.ok) throw new Error("Failed to load RMOs");
        const data = await res.json();
        setRmos(data.map(d => ({ value: d.id, label: d.name })));
      } catch (err) { console.error(err); } 
      finally { setLoadingMeta(m => ({ ...m, rmos: false })); }
    };
    loadRMOs();
  }, []);

// 👇 Yahan se Replace karo (useEffect lagana missing tha)
  useEffect(() => {
    const loadAuthorities = async () => {
      try {
        setLoadingAuthorities(true);
        const res = await customFetch(`/DiscountAuthDt`);
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        setDiscountAuthorities(data.map(a => ({ 
             value: a.id || a.Id,  
             label: `${a.type || a.Type}: ${a.authority || a.Authority}`,
             authName: a.authority || a.Authority 
        })));
      } catch (err) { console.error(err); } 
      finally { setLoadingAuthorities(false); }
    };
    
    loadAuthorities(); // 👈 Function ko run karna zaroori hai
  }, []);
  // 👆 Yahan tak

  

  // ✅ NEW: Fetch Active Packages for Dropdown
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoadingMeta(m => ({ ...m, packages: true }));
        const res = await customFetch(`/PackageMaster/active`);
        if (!res.ok) throw new Error("Failed to load packages");
        const data = await res.json();
        setPackages(data.map(p => ({ 
            value: p.id, 
            label: `${p.packageName} (₹${p.amount})`, 
            originalData: p 
        })));
      } catch (err) { console.error(err); } 
      finally { setLoadingMeta(m => ({ ...m, packages: false })); }
    };
    loadPackages();
  }, []);

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(res => res.json()).then(setCompanyDetails).catch(console.error);
    }
    const user = sessionStorage.getItem("loggedInUser"); 
    if(user) setLoggedInUser(user);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.closest('.react-select__menu')) return;

      const focusableSelector = 
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        formRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        formRef.current?.requestSubmit();
        return;
      }

      if (currentIndex >= 0) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fillPatientDetails = (data) => {
    if(!data) return;
    setForm(prev => ({
      ...prev,
      uhidNo: data.uhidNo || data.UHID_No || "",
      name: data.name || "",
      mobile: data.mobileNo || data.mobile || "",
      age: data.age ? String(data.age) : "",
      ageIn: data.ageIn || "Years",
      gender: data.gender || "Male",
      address: data.address || "",
      policeStation: data.policeStation || "", 
      city: data.city || "",
      state: data.stateName || "",
      pincode: data.pincode || "",
      title: data.title || "",
      careOf: data.careOff || "",
      relationWithPatient: data.relation || data.Relation || "Father",
      email: data.emailId || "",
      careOfMobile: data.alternateMobileNo || "",
      remarks: data.remarks || data.Remarks || "",
    }));
    if (data.stateName) setSelectedState({ value: data.stateName, label: data.stateName });
    if (data.city) setSelectedDistrict({ value: data.city, label: data.city });
    setSuccess("Patient details found & filled!");
  };

  const handleUhidSearch = async () => {
    if (!searchUhid) return;
    try {
      setLoading(true);
      const res = await customFetch(`/Opds/search-by-uhid?uhid=${searchUhid}`);
      if (res.ok) {
        const data = await res.json();
        fillPatientDetails(data);
      } else {
        setError("No patient found with this UHID.");
      }
    } catch (err) { setError("Search failed"); } 
    finally { setLoading(false); }
  };

  const loadNameOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) return callback([]);
    customFetch(`/Opds/search-by-name?name=${inputValue}`)
    .then(res => res.json())
    .then(data => {
        const options = data.map(p => ({
            value: p.uhidNo,
            label: `${p.name} (${p.gender}/${p.age}) - ${p.mobileNo}`,
            originalData: p
        }));
        callback(options);
    })
    .catch(() => callback([]));
  };

  const handleNameSelect = (selected) => {
    if (selected) fillPatientDetails(selected.originalData);
  };

  const handleMobileBlur = async (e) => {
    const mobile = e.target.value;
    const name = form.name;
    if (mobile.length === 10 && name.length > 2 && !form.uhidNo) {
        try {
            const res = await customFetch(`/Opds/check-duplicate?name=${name}&mobile=${mobile}`);
            const data = await res.json();
            if (data.found) {
                if(window.confirm(`Existing record found for ${data.name} (UHID: ${data.uhidNo}). Use details?`)) {
                    fillPatientDetails(data);
                }
            }
        } catch(err) { console.error(err); }
    }
  };

  const handleViewHistory = async () => {
    if (!form.uhidNo) return;
    setShowHistory(true);
    setLoadingHistory(true);
    try {
     const res = await customFetch(`/Opds/patient-history?uhid=${form.uhidNo}`);
      if(res.ok) {
         const data = await res.json();
         setHistoryData(data);
      } else {
         alert("Failed to load history");
      }
    } catch (err) { console.error(err); } 
    finally { setLoadingHistory(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "title") {
      setForm((prev) => ({ ...prev, gender: titleGenderMap[value] || prev.gender, title: value }));
    }
  };

  const handleSelectChange = (name, option) => {
    const value = option ? option.value : "";
    setForm(prev => {
      const newState = { ...prev, [name]: value };
      if (name === "title") newState.gender = titleGenderMap[value] || newState.gender;
      return newState;
    });
    if (name === "state") {
      setSelectedState(option); setSelectedDistrict(null); setForm(prev => ({ ...prev, city: "" }));
    }
    if (name === "city") setSelectedDistrict(option);
  };

  const handlePatientTypeChange = (e) => {
    const type = e.target.value;
    setPatientType(type);
    // Agar OPD select hua hai toh package clear kar do
    if (type === "OPD") {
        setForm(prev => ({ ...prev, packageId: "" }));
    }
  };

  useEffect(() => {
    const fee = Number(form.consultFee || 0);
    const disc = Number(form.discount || 0);
    const payable = Math.max(0, fee - (fee * disc) / 100);
    setForm(prev => ({ ...prev, payable: payable ? String(payable) : "" }));
  }, [form.consultFee, form.discount]);

  useEffect(() => {
    const payable = Number(form.payable || 0);
    const paid = Number(form.paid || 0);
    const due = payable - paid;
    setForm(prev => ({ ...prev, due: String(due) })); 
  }, [form.payable, form.paid]);

  const required = useMemo(() => ["name", "mobile", "gender", "age", "ageIn", "address", "paymentMode"], []);

  const validate = () => {
    const missing = required.filter(k => !String(form[k] ?? "").trim());
    if (missing.length) return `Please fill required fields: ${missing.join(", ")}`;
    if (!form.age || Number(form.age) <= 0) return "Age must be greater than 0";
    if (!/^\d{10}$/.test(form.mobile)) return "Mobile must be 10 digits";
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) {
        return "Pincode must be 6 digits";
    }

    if (form.discount && Number(form.discount) > 0 && !form.discountAuthority) {
      return "Discount diya gaya hai, isliye Discount Authority select karna zaroori hai.";
    }
    return "";
  };

  const toPayload = () => {
      // Find package details if selected
      const selectedPkg = packages.find(p => p.value === form.packageId)?.originalData;
      // 👇 1. Selected Authority ko find karo
   // 👇 Selected Authority ko dhoondhne ke liye (Submit karte waqt)
const selectedAuth = discountAuthorities.find(a => String(a.value) === String(form.discountAuthority));

      return {
        UhidNo: form.uhidNo || null,
        UHID_No: form.uhidNo || null,
        OpdDate: form.registrationDate,
        IPD_Date: form.registrationDate,
        DoctorId: form.doctorId ? Number(form.doctorId) : null,
        DepartmentId: form.departmentId ? Number(form.departmentId) : null,
        RmoId: form.rmoId ? Number(form.rmoId) : null,
        ChargeName: null,
        ConsultFee: form.consultFee ? Number(form.consultFee) : null,
        Problems: form.appointmentReason,
        CreatedBy: loggedInUser,
        Bp: form.bp,
        Weights: form.weight,
        Heights: form.height,
        Oxy: form.oxygen,
        Pulses: form.pulse,
        Temps: form.temperature,
        Rbs: form.rbs,
        ReferedBy: form.referredBy,
      
        SendBy: form.sendBy,

AuthorisedBy: selectedAuth ? selectedAuth.authName : null, // Naam chala gaya
        DiscountAuthId: form.discountAuthority ? Number(form.discountAuthority) : null, // ID chali gayi
        
        Remarks: form.remarks, 
        PayMode: form.paymentMode,
        Received: form.paid ? Number(form.paid) : null,
        Discount: form.discount ? Number(form.discount) : null,
        Payable: form.payable ? Number(form.payable) : null,
        Dues: form.due ? Number(form.due) : null,
        DisRequestBy: form.requestedBy,
        RegDate: form.registrationDate,
        Title: form.title,
        Name: form.name,
        MobileNo: form.mobile,
        Gender: form.gender,
        Age: form.age ? Number(form.age) : null,
        AgeIn: form.ageIn,
        CareOff: form.careOf,
        RelationWithPatient: form.relationWithPatient,
        AlternateMobileNo: form.careOfMobile,
        Address: form.address,
        PoliceStation: form.policeStation, 
        City: form.city,
        StateName: form.state,
        Pincode: form.pincode,
        EmailId: form.email,
        
        // ✅ Package Details Map for IPD Request
        ...(patientType === "IPD" && {
            PackageId: form.packageId ? Number(form.packageId) : null,
            PackageName: selectedPkg ? selectedPkg.packageName : null,
            PackageAmount: selectedPkg ? selectedPkg.amount : null,
        })
      };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const msg = validate();
    if (msg) { setError(msg); return; }
    try {
      setLoading(true);
      const endpoint = patientType === "IPD" ? "Ipds" : "Opds";
      const res = await customFetch(`/${endpoint}`, {
        method: "POST",
        body: JSON.stringify(toPayload()),
      });
      if (!res.ok) throw new Error(await res.text());
      const savedRecord = await res.json();
      setSavedData(savedRecord);
      setShowReceipt(true);
      handleReset();
      setSuccess(`${patientType} appointment saved successfully`);
    } catch (err) { setError(err.message || "Submit failed"); } 
    finally { setLoading(false); }
  };

  const handleReset = () => {
    setForm(initialForm);
    setSelectedState(null);
    setSelectedDistrict(null);
    setSearchUhid("");
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base, 
      minHeight: clamp(26, 2.2, 30) + 'px',
      height: clamp(26, 2.2, 30) + 'px', 
      fontSize: clamp(10, 0.82, 12) + 'px', 
      borderRadius: clamp(4, 0.4, 6) + 'px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#dde2e8',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ 
      ...base, 
      padding: `0 ${clamp(6, 0.6, 8)}px` 
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ 
      ...base, 
      height: clamp(26, 2.2, 30) + 'px' 
    }),
    dropdownIndicator: (base) => ({ 
      ...base, 
      padding: '2px 6px' 
    }),
    clearIndicator: (base) => ({ 
      ...base, 
      padding: '2px 6px' 
    }),
    menu: (base) => ({ 
      ...base, 
      zIndex: 9999, 
      borderRadius: clamp(4, 0.4, 6) + 'px', 
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)', 
      border: '1px solid #e0e6ed' 
    }),
    option: (base, state) => ({
      ...base, 
      fontSize: clamp(10, 0.82, 12) + 'px',
      backgroundColor: state.isSelected ? 'var(--theme-color, #0088cc)' : state.isFocused ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' : 'white',
      color: state.isSelected ? 'white' : '#2c3e50', 
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)' },
    }),
    placeholder: (base) => ({ 
      ...base, 
      color: '#adb5bd',
      fontSize: clamp(10, 0.78, 11) + 'px'
    })
  };

  const hasDiscount = form.discount && Number(form.discount) > 0;

  return (
    <div className="patient-container">
      <div className="patient-card">

        {/* ── Header ── */}
        <div className="patient-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-calendar-plus"></i></div>
            <div className="header-text">
              <h5>Patient Registration &amp; Appointment</h5>
              <p>Book OPD/IPD appointments</p>
            </div>
          </div>
          {form.uhidNo && (
            <button type="button" className="btn-reset ms-auto text-primary" onClick={handleViewHistory}>
              <i className="bi bi-clock-history"></i> History
            </button>
          )}
        </div>

        {error   && <div className="alert-custom alert-danger  slide-in"><i className="bi bi-exclamation-triangle-fill"></i><span>{error}</span>  <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button></div>}
        {success && <div className="alert-custom alert-success slide-in"><i className="bi bi-check-circle-fill"></i>        <span>{success}</span><button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button></div>}

        <div className="patient-body">
          <div className="form-section">
            <form ref={formRef} onSubmit={handleSubmit} className="patient-form">

              {/* ══ Top Row: OPD/IPD + UHID Search ══ */}
              <div className="form-row" style={{ alignItems: "center" }}>
                <div className="form-group col-span-2">
                  <div className="type-selector" style={{ marginBottom: 0, height: '30px' }}>
                    <label className="radio-label">
                      <input type="radio" name="patientType" value="OPD" checked={patientType === "OPD"} onChange={handlePatientTypeChange} ref={opdRadioRef} />
                      <span><i className="bi bi-hospital"></i> OPD</span>
                    </label>
                    <label className="radio-label">
                      <input type="radio" name="patientType" value="IPD" checked={patientType === "IPD"} onChange={handlePatientTypeChange} />
                      <span><i className="bi bi-house-heart"></i> IPD</span>
                    </label>
                  </div>
                </div>
                <div className="form-group col-span-2" style={{ flexDirection: 'row', gap: '5px' }}>
                  <input 
                    className="form-control-custom" 
                    placeholder="Search by UHID..." 
                    value={searchUhid} 
                    onChange={(e) => setSearchUhid(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUhidSearch())}
                  />
                  <button type="button" className="btn-submit" style={{ padding: '5px 10px', whiteSpace: 'nowrap' }} onClick={handleUhidSearch}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </div>

              {/* ══ Row 1: Title | Name | Age | Age In | Gender | Mobile | Date ══ */}
              <div className="form-row row-personal">
                <div className="form-group">
                  <label><i className="bi bi-person-circle"></i> Title <span className="required">*</span></label>
                  <Select options={titleOptions} value={titleOptions.find(o => o.value === form.title)} onChange={(o) => handleSelectChange("title", o)} styles={selectStyles} placeholder="—" menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-person"></i> Name <span className="required">*</span></label>
                  <AsyncSelect
                    cacheOptions loadOptions={loadNameOptions} defaultOptions={false} isClearable
                    value={form.name ? { label: form.name, value: form.name } : null}
                    onChange={(sel) => { sel ? handleNameSelect(sel) : setForm(prev => ({ ...prev, name: "", uhidNo: "" })); }}
                    onInputChange={(val, { action }) => { if (action === 'input-change') setForm(prev => ({ ...prev, name: val, uhidNo: "" })); }}
                    styles={selectStyles} placeholder="Type Name..." menuPortalTarget={document.body}
                  />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-123"></i> Age <span className="required">*</span></label>
                  <input name="age" value={form.age} onChange={handleChange} required placeholder="25" type="number" className="form-control-custom" min="0" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-hourglass"></i> Age In</label>
                  <Select options={ageInOptions} value={ageInOptions.find(o => o.value === form.ageIn)} onChange={(o) => handleSelectChange("ageIn", o)} styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-gender-ambiguous"></i> Gender <span className="required">*</span></label>
                  <Select options={genderOptions} value={genderOptions.find(o => o.value === form.gender)} onChange={(o) => handleSelectChange("gender", o)} styles={selectStyles} placeholder="Select" menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-phone"></i> Mobile <span className="required">*</span></label>
                  <input name="mobile" value={form.mobile} required onChange={handleChange} onBlur={handleMobileBlur} placeholder="98XXXXXXXX" maxLength="10" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-calendar-event"></i> Reg. Date <span className="required">*</span></label>
                  <input name="registrationDate" value={form.registrationDate} required onChange={handleChange} type="date" className="form-control-custom" disabled={loading} />
                </div>
              </div>

              {/* ══ Row 2: Care Of | Relation | Care Of Mobile | Email | Reason ══ */}
              <div className="form-row row-contact">
                <div className="form-group">
                  <label><i className="bi bi-person-lines-fill"></i> Care Of</label>
                  <input name="careOf" value={form.careOf} onChange={handleChange} placeholder="Guardian Name" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-diagram-3"></i> Relation</label>
                  <Select options={relationOptions} value={relationOptions.find(o => o.value === form.relationWithPatient)} onChange={(o) => handleSelectChange("relationWithPatient", o)} styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-telephone"></i> Care Of Mobile</label>
                  <input name="careOfMobile" value={form.careOfMobile} onChange={handleChange} placeholder="98XXXXXXXX" maxLength="10" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-envelope"></i> Email</label>
                  <input name="email" value={form.email} onChange={handleChange} placeholder="email@example.com" type="email" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-chat-left-text"></i> Reason</label>
                  <input name="appointmentReason" value={form.appointmentReason} placeholder="Fever, Cough" onChange={handleChange} className="form-control-custom" disabled={loading} />
                </div>
              </div>

              {/* ══ Row 3: Address | Police Station | State | District | Pincode ══ */}
              <div className="form-row row-address">
                <div className="form-group address-col">
                  <label><i className="bi bi-geo-alt"></i> Address <span className="required">*</span></label>
                  <input name="address" value={form.address} placeholder="House No, Locality" required onChange={handleChange} className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-shield-lock"></i> Police Station</label>
                  <input name="policeStation" value={form.policeStation} onChange={handleChange} placeholder="Nearest Thana" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-pin-map"></i> State <span className="required">*</span></label>
                  <Select options={states} value={selectedState} onChange={(o) => handleSelectChange("state", o)} required placeholder="Select" isClearable isSearchable isLoading={loadingMeta.states} styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-pin"></i> District <span className="required">*</span></label>
                  <Select options={districts} value={selectedDistrict} onChange={(o) => handleSelectChange("city", o)} required placeholder="Select" isClearable isSearchable isLoading={loadingMeta.districts} isDisabled={!selectedState} styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-mailbox"></i> Pincode </label>
                  <input name="pincode" value={form.pincode} onChange={handleChange} placeholder="273001" maxLength="6" className="form-control-custom" disabled={loading} />
                </div>
              </div>

              {/* ══ Row 4: Department | RMO | Doctor | Referred By ══ */}
              <div className="form-row row-dept">
                <div className="form-group">
                  <label><i className="bi bi-grid"></i> Department <span className="required">*</span></label>
                  <Select options={departments} value={departments.find(o => o.value === form.departmentId)} required onChange={(o) => handleSelectChange("departmentId", o)} isLoading={loadingMeta.departments} placeholder="Select" styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-person-fill-add"></i> RMO</label>
                  <Select options={rmos} value={rmos.find(o => o.value === form.rmoId) || null} onChange={(o) => handleSelectChange("rmoId", o)} isLoading={loadingMeta.rmos} placeholder="Select RMO" isClearable styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-person-badge-fill"></i> Doctor <span className="required">*</span></label>
                  <Select options={doctors} value={doctors.find(o => o.value === form.doctorId)} required onChange={(o) => handleSelectChange("doctorId", o)} isLoading={loadingMeta.doctors} placeholder="Select Doctor" styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-arrow-repeat"></i> Referred By</label>
                  <input name="referredBy" value={form.referredBy} placeholder="Dr. Name" onChange={handleChange} className="form-control-custom" disabled={loading} />
                </div>
              </div>

              {/* ══ Row 5: Vitals ══ */}
              <div className="form-row row-vitals">
                <div className="form-group">
                  <label><i className="bi bi-distribute-vertical"></i> Wt (kg)</label>
                  <input name="weight" value={form.weight} onChange={handleChange} placeholder="65" type="number" step="0.1" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-arrows-vertical"></i> Ht (cm)</label>
                  <input name="height" value={form.height} onChange={handleChange} placeholder="170" type="number" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-activity"></i> BP</label>
                  <input name="bp" value={form.bp} onChange={handleChange} placeholder="120/80" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-wind"></i> Oxy (%)</label>
                  <input name="oxygen" value={form.oxygen} onChange={handleChange} placeholder="98" type="number" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-heart"></i> Pulse</label>
                  <input name="pulse" value={form.pulse} onChange={handleChange} placeholder="72" type="number" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-thermometer-half"></i> Temp</label>
                  <input name="temperature" value={form.temperature} placeholder="98.6" onChange={handleChange} type="number" step="0.1" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-droplet-half"></i> RBS</label>
                  <input name="rbs" value={form.rbs} onChange={handleChange} placeholder="100" type="number" className="form-control-custom" disabled={loading} />
                </div>
              </div>

              {/* ══ Row 6: Payment Details ══ */}
              <div className="form-row row-payment">
                <div className="form-group">
                  <label className="text-primary"><i className="bi bi-cash-coin"></i> Fee</label>
                  <input name="consultFee" value={form.consultFee} onChange={handleChange} placeholder="500" type="number" className="form-control-custom input-primary" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-percent"></i> Disc</label>
                  <input name="discount" value={form.discount} onChange={handleChange} placeholder="10" type="number" min="0" max="100" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-calculator"></i> Payable</label>
                  <input name="payable" value={form.payable} type="number" className="form-control-custom bg-light" readOnly />
                </div>
                <div className="form-group">
                  <label className="text-success"><i className="bi bi-check-circle"></i> Paid</label>
                  <input name="paid" value={form.paid} onChange={handleChange} type="number" className="form-control-custom input-success" disabled={loading} />
                </div>
                <div className="form-group">
                  <label className="text-danger"><i className="bi bi-exclamation-circle"></i> Due</label>
                  <input name="due" value={form.due} type="number" className="form-control-custom input-danger" readOnly />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-credit-card"></i> Pay Mode <span className="required">*</span></label>
                  <Select options={paymentModeOptions} value={paymentModeOptions.find(o => o.value === form.paymentMode)} required onChange={(o) => handleSelectChange("paymentMode", o)} styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-person-workspace"></i> Req By</label>
                  <input name="requestedBy" value={form.requestedBy} onChange={handleChange} placeholder="Staff" className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
  <label className="text-warning"><i className="bi bi-shield-check"></i> Auth</label>
  <Select 
     options={discountAuthorities} 
     value={discountAuthorities.find(o => String(o.value) === String(form.discountAuthority)) || null} 
     onChange={(o) => handleSelectChange("discountAuthority", o)} 
     styles={selectStyles} 
     menuPortalTarget={document.body} 
     placeholder={hasDiscount ? "Required *" : "Opt"} 
     isDisabled={!hasDiscount || loadingAuthorities} // loading add kiya taaki api response aane tak click na ho
     isLoading={loadingAuthorities}
     isClearable 
  />
</div>
              </div>

              {/* ══ Row 7 (UPDATED): Package | Send By | Remarks ══ */}
              {/* Dynamic class added based on patientType to handle grid columns smoothly */}
              <div className={`form-row ${patientType === "IPD" ? "row-remarks-ipd" : "row-remarks-opd"}`}>
                
                {/* ✅ DYNAMIC PACKAGE DROPDOWN (ONLY VISIBLE FOR IPD) */}
                {patientType === "IPD" && (
                    <div className="form-group">
                      <label className="text-primary"><i className="bi bi-box-seam"></i> IPD Package</label>
                      <Select 
                        options={packages} 
                        value={packages.find(o => o.value === form.packageId) || null} 
                        onChange={(o) => handleSelectChange("packageId", o)} 
                        isLoading={loadingMeta.packages} 
                        placeholder="Select Package (Optional)" 
                        isClearable 
                        styles={selectStyles} 
                        menuPortalTarget={document.body} 
                      />
                    </div>
                )}

                <div className="form-group">
                  <label><i className="bi bi-truck"></i> Send By</label>
                  <input name="sendBy" value={form.sendBy} placeholder="Ambulance / Family" onChange={handleChange} className="form-control-custom" disabled={loading} />
                </div>
                <div className="form-group">
                  <label><i className="bi bi-card-text"></i> Remarks</label>
                  <input name="remarks" value={form.remarks} placeholder="Enter any additional notes..." onChange={handleChange} className="form-control-custom" disabled={loading} />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-reset" onClick={handleReset} disabled={loading}>
                  <i className="bi bi-arrow-clockwise"></i> Reset
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading
                    ? (<><span className="spinner-border spinner-border-sm"></span> Saving...</>)
                    : (<><i className="bi bi-check-circle"></i> Book Appointment</>)
                  }
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      {companyDetails && (
        <ReceiptModal show={showReceipt} data={savedData} companyDetails={companyDetails} loggedInUser={loggedInUser} onClose={() => setShowReceipt(false)} onPrint={() => window.print()} />
      )}

      <PatientHistoryModal 
        show={showHistory}
        data={historyData}
        loading={loadingHistory}
        patientName={form.name}
        uhid={form.uhidNo}
        onClose={() => setShowHistory(false)}
      />

      <style jsx>{`
        .patient-container { padding: 0; }

        .patient-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 15px rgba(0, 136, 204, 0.08);
          border: 1px solid #e0e6ed;
          overflow: hidden;
        }

        .patient-header {
          background: var(--theme-color, #0088cc);
          padding: 9px 18px;
          color: white;
          display: flex;
          align-items: center;
          min-height: 0;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }

        .header-icon {
          width: 34px; height: 34px;
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 17px;
          backdrop-filter: blur(10px);
          flex-shrink: 0;
        }

        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; line-height: 1.2; }
        .header-text p  { margin: 1px 0 0 0; opacity: 0.88; font-size: 11px; line-height: 1.2; }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .slide-in { animation: slideIn 0.3s ease; }

        .alert-custom {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 18px; margin: 12px 18px 0;
          border-radius: 7px; font-size: 12px;
        }
        .alert-danger  { background: #fff5f5; border: 1px solid #ffb8b8; color: #d63031; }
        .alert-success { background: #f0fff4; border: 1px solid #a7f3d0; color: #047857; }
        .alert-custom i { font-size: 14px; }
        .alert-close {
          margin-left: auto; background: none; border: none;
          font-size: 15px; cursor: pointer; opacity: 0.6; padding: 0; color: inherit;
        }
        .alert-close:hover { opacity: 1; }

        .patient-body  { padding: 14px 18px; }
        .form-section  { background: #ffffff; }
        .patient-form  { display: flex; flex-direction: column; gap: 10px; }

        .type-selector { display: flex; gap: 18px; }
        .radio-label {
          display: flex; align-items: center; gap: 5px;
          cursor: pointer; font-size: 13px; font-weight: 500;
        }
        .radio-label input[type="radio"] { cursor: pointer; }
        .radio-label span { display: flex; align-items: center; gap: 4px; }

        .form-row {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: clamp(6px, 0.7vw, 10px);
          align-items: end;
        }

        /* ════ ROW GRIDS (1200px Base) ════ */
        .form-row.row-personal { grid-template-columns: 0.5fr 2fr 0.5fr 0.7fr 1fr 1fr 1fr; }
        .form-row.row-contact  { grid-template-columns: 1fr 1fr 1fr 1.5fr 1fr; }
        .form-row.row-address  { grid-template-columns: 2.2fr 1fr 1fr 1fr 1fr; }
        .form-row.row-dept     { grid-template-columns: 1fr 1fr 2.4fr 1fr; }
        .form-row.row-vitals   { grid-template-columns: repeat(7, minmax(0, 1fr)); }
        .form-row.row-payment  { grid-template-columns: 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr 1.2fr 1.2fr; }
        
        /* ✅ DYNAMIC REMARKS ROW (OPD vs IPD) */
        .form-row.row-remarks-opd  { grid-template-columns: 1fr 4fr; }
        .form-row.row-remarks-ipd  { grid-template-columns: 1.5fr 1fr 3fr; } /* Added space for Package Dropdown */

        .form-group {
          display: flex; flex-direction: column;
          gap: clamp(2px, 0.25vw, 4px);
          min-width: 0;
        }
        .form-group.col-span-2 { grid-column: span 2; }
        .form-group.col-span-3 { grid-column: span 3; }

        .form-group label {
          display: flex; align-items: center;
          gap: clamp(3px, 0.35vw, 5px);
          font-size: clamp(9px, 0.78vw, 11px);
          font-weight: 500; color: #495057;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .form-group label i { font-size: 11px; color: var(--theme-color, #0088cc); flex-shrink: 0; }

        .text-primary { color: var(--theme-color, #0066aa) !important; }
        .text-success  { color: #047857 !important; }
        .text-danger   { color: #dc2626 !important; }
        .text-warning  { color: #d97706 !important; }
        .required      { color: #dc2626; }

        .form-control-custom {
          padding: clamp(3px, 0.35vw, 5px) clamp(5px, 0.55vw, 8px);
          font-size: clamp(10px, 0.82vw, 12px);
          border: 1px solid #dde2e8;
          border-radius: clamp(4px, 0.4vw, 6px);
          outline: none; transition: all 0.2s ease;
          background: white; color: #2c3e50;
          width: 100%; min-width: 0;
          height: clamp(26px, 2.2vw, 30px);
          box-sizing: border-box;
        }
        .form-control-custom:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
        }
        select.form-control-custom {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232c3e50' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat; background-position: right 8px center; background-size: 12px; padding-right: 24px;
        }
        select.form-control-custom:hover { border-color: var(--theme-color, #0088cc); }
        .form-control-custom:disabled { background: #f8f9fa; cursor: not-allowed; opacity: 0.7; }
        .form-control-custom::placeholder { color: #adb5bd; font-size: clamp(9px, 0.75vw, 11px); }

        .input-primary { border-color: #90caf9; background: #e3f2fd; }
        .input-success { border-color: #a7f3d0; background: #d1f4e0; }
        .input-danger  { border-color: #fecaca; background: #fee2e2; }
        .input-warning { border-color: #fde68a; background: #fef3c7; }
        .bg-light      { background: #f1f3f5 !important; cursor: not-allowed; }

        .form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }

        .btn-reset {
          display: flex; align-items: center; gap: 5px;
          padding: 6px 16px; background: white; color: #6c757d;
          border: 1px solid #dee2e6; border-radius: 6px;
          font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.2s ease;
        }
        .btn-reset:hover:not(:disabled) { background: #f8f9fa; border-color: #adb5bd; }

        .btn-submit {
          display: flex; align-items: center; gap: 5px;
          padding: 6px 20px;
          background: var(--theme-color, #0088cc);
          color: white; border: none; border-radius: 6px;
          font-size: 12px; font-weight: 600; cursor: pointer;
          transition: all 0.3s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .btn-submit:hover:not(:disabled) { opacity: 0.9; box-shadow: 0 4px 12px rgba(0,0,0,0.15); transform: translateY(-1px); }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .spinner-border {
          display: inline-block; width: 12px; height: 12px;
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 50%; border-top-color: white;
          animation: spin 0.8s linear infinite;
        }
        .spinner-border-sm { width: 11px; height: 11px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ════ RESPONSIVE BREAKPOINTS ════ */

        @media (max-width: 1199px) and (min-width: 900px) {
          .form-row.row-personal   { grid-template-columns: 0.5fr 1.8fr 0.5fr 0.7fr 1fr 1fr 1fr; }
          .form-row.row-contact    { grid-template-columns: 1fr 1fr 1fr 1.5fr 1fr; }
          .form-row.row-address    { grid-template-columns: 2fr 1fr 1fr 1fr 1fr; }
          .form-row.row-dept       { grid-template-columns: 1fr 1fr 2fr 1fr; }
          .form-row.row-vitals     { grid-template-columns: repeat(5, minmax(0, 1fr)); }
          .form-row.row-payment    { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          
          .form-row.row-remarks-opd  { grid-template-columns: 1fr 4fr; }
          .form-row.row-remarks-ipd  { grid-template-columns: 1.5fr 1fr 3fr; }
        }

        @media (max-width: 899px) and (min-width: 700px) {
          .form-row.row-personal   { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .form-row.row-contact    { grid-template-columns: 1fr 1fr 1fr 1fr; }
          .form-row.row-address    { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .form-row.row-dept       { grid-template-columns: 1fr 1fr 2fr 1fr; }
          .form-row.row-vitals     { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .form-row.row-payment    { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          
          .form-row.row-remarks-opd  { grid-template-columns: 1fr 3fr; }
          .form-row.row-remarks-ipd  { grid-template-columns: 1fr 1fr 2fr; }
          .address-col             { grid-column: span 2; }
        }

        @media (max-width: 699px) and (min-width: 500px) {
          .form-row.row-personal   { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .form-row.row-contact    { grid-template-columns: 1fr 1fr 1fr; }
          .form-row.row-address    { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .form-row.row-dept       { grid-template-columns: 1fr 1fr 1fr; }
          .form-row.row-vitals     { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .form-row.row-payment    { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          
          .form-row.row-remarks-opd  { grid-template-columns: 1fr 2fr; }
          .form-row.row-remarks-ipd  { grid-template-columns: 1fr 1fr; } /* Stacked slightly */
          .address-col             { grid-column: span 3; }
        }

        @media (max-width: 499px) {
          .patient-body { padding: 10px 12px; }
          .form-row.row-personal   { grid-template-columns: 1fr 1fr; }
          .form-row.row-contact    { grid-template-columns: 1fr 1fr; }
          .form-row.row-address    { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .form-row.row-dept       { grid-template-columns: 1fr 1fr; }
          .form-row.row-vitals     { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .form-row.row-payment    { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          
          .form-row.row-remarks-opd, .form-row.row-remarks-ipd { grid-template-columns: 1fr; }
          .address-col             { grid-column: span 2; }
          .form-group.col-span-2,
          .form-group.col-span-3  { grid-column: span 1; }
          .header-text h5 { font-size: 13px; }
        }
      `}</style>
    </div>
  );
};

export default PatientRegister;